import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield,
  ArrowLeft,
  FileText,
  Image,
  Video,
  Loader2,
  Calendar,
  Building2,
  ChevronRight,
  Wrench,
  RefreshCw,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  User,
  Phone,
  Mail,
  Upload,
  Briefcase,
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  UserPlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  submitted: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  insurance_notified: 'bg-purple-100 text-purple-700',
  contractor_assigned: 'bg-amber-100 text-amber-700',
  repair_in_progress: 'bg-orange-100 text-orange-700',
  completed: 'bg-emerald-100 text-emerald-700',
  claim_closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_NAMES = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  insurance_notified: 'Insurance Notified',
  contractor_assigned: 'Contractor Assigned',
  repair_in_progress: 'Repair In Progress',
  completed: 'Completed',
  claim_closed: 'Claim Closed',
  rejected: 'Rejected',
};

export default function AdminInsuranceClaims() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [claimStatuses, setClaimStatuses] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('claims');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  const [statusUpdate, setStatusUpdate] = useState({ status: '', note: '', notify_submitter: true });
  const [assignData, setAssignData] = useState({ contractor_id: '', repair_scheduled_date: '', notes: '' });

  // Insurance Agents State
  const [insuranceAgents, setInsuranceAgents] = useState([]);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({
    agent_name: '',
    company_name: '',
    email: '',
    phone: '',
    license_number: '',
    policy_number: '',
    policy_type: 'comprehensive',
    coverage_amount: '',
    policy_start_date: '',
    policy_expiry_date: '',
    notes: ''
  });
  const [savingAgent, setSavingAgent] = useState(false);

  const token = localStorage.getItem('session_token');
  const buildingId = hoaId || user?.hoa_id || localStorage.getItem('hoa_id');

  useEffect(() => {
    if (buildingId) {
      fetchData();
    }
  }, [buildingId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Get claim types & statuses
      const [typesRes, statusesRes] = await Promise.all([
        fetch(`${API_URL}/api/insurance/claim-types`, { headers }),
        fetch(`${API_URL}/api/insurance/claim-statuses`, { headers })
      ]);
      
      if (typesRes.ok) setClaimTypes(await typesRes.json());
      if (statusesRes.ok) setClaimStatuses(await statusesRes.json());
      
      // Get building claims
      const claimsRes = await fetch(`${API_URL}/api/insurance/claims/building/${buildingId}`, { headers });
      if (claimsRes.ok) {
        setClaims(await claimsRes.json());
      }
      
      // Get analytics
      const analyticsRes = await fetch(`${API_URL}/api/insurance/analytics/${buildingId}`, { headers });
      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
      
      // Get contractors
      const contractorsRes = await fetch(`${API_URL}/api/contractors/list?status=approved`, { headers });
      if (contractorsRes.ok) {
        setContractors(await contractorsRes.json());
      }
      
      // Get insurance agents for this building
      const agentsRes = await fetch(`${API_URL}/api/insurance/agents/${buildingId}`, {
        credentials: 'include'
      });
      if (agentsRes.ok) {
        const agentsData = await agentsRes.json();
        setInsuranceAgents(agentsData.agents || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClaim = async (claim) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/${claim.claim_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedClaim(await res.json());
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load claim details');
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/${selectedClaim.claim_id}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusUpdate)
      });

      if (res.ok) {
        toast.success('Status updated');
        setShowStatusDialog(false);
        setStatusUpdate({ status: '', note: '', notify_submitter: true });
        handleViewClaim(selectedClaim);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignContractor = async () => {
    if (!assignData.contractor_id) {
      toast.error('Please select a contractor');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/${selectedClaim.claim_id}/assign-contractor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignData)
      });

      if (res.ok) {
        toast.success('Contractor assigned');
        setShowAssignDialog(false);
        setAssignData({ contractor_id: '', repair_scheduled_date: '', notes: '' });
        handleViewClaim(selectedClaim);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign contractor');
    }
  };

  // Insurance Agent Functions
  const openAddAgentDialog = () => {
    setEditingAgent(null);
    setAgentForm({
      agent_name: '',
      company_name: '',
      email: '',
      phone: '',
      license_number: '',
      policy_number: '',
      policy_type: 'comprehensive',
      coverage_amount: '',
      policy_start_date: '',
      policy_expiry_date: '',
      notes: ''
    });
    setShowAgentDialog(true);
  };

  const openEditAgentDialog = (agent) => {
    setEditingAgent(agent);
    setAgentForm({
      agent_name: agent.agent_name || '',
      company_name: agent.company_name || '',
      email: agent.email || '',
      phone: agent.phone || '',
      license_number: agent.license_number || '',
      policy_number: agent.policy_number || '',
      policy_type: agent.policy_type || 'comprehensive',
      coverage_amount: agent.coverage_amount?.toString() || '',
      policy_start_date: agent.policy_start_date || '',
      policy_expiry_date: agent.policy_expiry_date || '',
      notes: agent.notes || ''
    });
    setShowAgentDialog(true);
  };

  const handleSaveAgent = async () => {
    if (!agentForm.agent_name || !agentForm.company_name || !agentForm.email) {
      toast.error('Please fill in required fields (Agent Name, Company, Email)');
      return;
    }

    setSavingAgent(true);
    try {
      const payload = {
        ...agentForm,
        hoa_id: buildingId,
        coverage_amount: agentForm.coverage_amount ? parseFloat(agentForm.coverage_amount) : 0
      };

      const url = editingAgent 
        ? `${API_URL}/api/insurance/agents/${editingAgent.agent_id}`
        : `${API_URL}/api/insurance/agents/add`;
      
      const res = await fetch(url, {
        method: editingAgent ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingAgent ? 'Insurance agent updated' : 'Insurance agent added');
        setShowAgentDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save insurance agent');
      }
    } catch (error) {
      toast.error('Failed to save insurance agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const handleDeleteAgent = async (agent) => {
    if (!confirm(`Remove ${agent.agent_name} as insurance agent?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/insurance/agents/${agent.agent_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Insurance agent removed');
        fetchData();
      } else {
        toast.error('Failed to remove insurance agent');
      }
    } catch (error) {
      toast.error('Failed to remove insurance agent');
    }
  };

  const filteredClaims = claims.filter(c => {
    if (filterStatus && c.status !== filterStatus) return false;
    if (filterType && c.claim_type !== filterType) return false;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const getClaimTypeName = (code) => {
    const type = claimTypes.find(t => t.code === code);
    return type?.name || code;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="admin-insurance-claims">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Shield className="w-8 h-8 text-indigo-600" />
                Insurance Claims Management
              </h1>
              <p className="text-slate-500 mt-1">Manage and track insurance claims for your building</p>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{analytics.total_claims}</p>
                    <p className="text-xs text-slate-500">Total Claims</p>
                  </div>
                  <Shield className="w-8 h-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{analytics.open_claims}</p>
                    <p className="text-xs text-slate-500">Open Claims</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{analytics.resolved}</p>
                    <p className="text-xs text-slate-500">Resolved</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(analytics.total_value)}</p>
                    <p className="text-xs text-slate-500">Total Value</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="claims">Claims ({claims.length})</TabsTrigger>
            <TabsTrigger value="agents" data-testid="agents-tab">
              <UserPlus className="w-4 h-4 mr-1" />
              Insurance Agents ({insuranceAgents.length})
            </TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Claims Tab */}
          <TabsContent value="claims">
            {/* Filters */}
            <div className="flex gap-4 mb-4">
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {claimStatuses.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {claimTypes.map(t => (
                    <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredClaims.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Claims</h3>
                  <p className="text-slate-500">No insurance claims match your filters.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredClaims.map(claim => (
                  <Card 
                    key={claim.claim_id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleViewClaim(claim)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold">{claim.claim_reference}</h3>
                            <Badge className={STATUS_COLORS[claim.status]}>
                              {STATUS_NAMES[claim.status]}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{getClaimTypeName(claim.claim_type)}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span><User className="w-3 h-3 inline mr-1" />{claim.submitter_name}</span>
                            <span><Building2 className="w-3 h-3 inline mr-1" />Unit {claim.unit_number}</span>
                            <span><Calendar className="w-3 h-3 inline mr-1" />{formatDate(claim.incident_date)}</span>
                            {claim.estimated_value && (
                              <span><DollarSign className="w-3 h-3 inline mr-1" />{formatCurrency(claim.estimated_value)}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Insurance Agents Tab */}
          <TabsContent value="agents">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-indigo-600" />
                  Insurance Agents / Brokers
                </CardTitle>
                <Button onClick={openAddAgentDialog} data-testid="add-insurance-agent-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Insurance Agent
                </Button>
              </CardHeader>
              <CardContent>
                {insuranceAgents.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Insurance Agents</h3>
                    <p className="text-slate-500 mb-4">Add an insurance agent or broker for this building.</p>
                    <Button onClick={openAddAgentDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Agent
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {insuranceAgents.map((agent) => (
                      <Card key={agent.agent_id} className="border border-slate-200" data-testid={`agent-card-${agent.agent_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                  <User className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{agent.agent_name}</h4>
                                  <p className="text-sm text-slate-500">{agent.company_name}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                                <div>
                                  <p className="text-slate-500 text-xs">Email</p>
                                  <a href={`mailto:${agent.email}`} className="text-indigo-600 hover:underline">
                                    {agent.email}
                                  </a>
                                </div>
                                {agent.phone && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Phone</p>
                                    <a href={`tel:${agent.phone}`} className="text-slate-700">
                                      {agent.phone}
                                    </a>
                                  </div>
                                )}
                                {agent.policy_number && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Policy Number</p>
                                    <p className="text-slate-700 font-medium">{agent.policy_number}</p>
                                  </div>
                                )}
                                {agent.policy_type && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Policy Type</p>
                                    <Badge variant="outline" className="capitalize">
                                      {agent.policy_type}
                                    </Badge>
                                  </div>
                                )}
                                {agent.coverage_amount > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Coverage Amount</p>
                                    <p className="text-slate-700 font-medium">R{agent.coverage_amount?.toLocaleString()}</p>
                                  </div>
                                )}
                                {agent.policy_expiry_date && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Policy Expires</p>
                                    <p className={`font-medium ${
                                      new Date(agent.policy_expiry_date) < new Date() 
                                        ? 'text-red-600' 
                                        : new Date(agent.policy_expiry_date) < new Date(Date.now() + 30*24*60*60*1000)
                                          ? 'text-amber-600'
                                          : 'text-slate-700'
                                    }`}>
                                      {formatDate(agent.policy_expiry_date)}
                                    </p>
                                  </div>
                                )}
                                {agent.license_number && (
                                  <div>
                                    <p className="text-slate-500 text-xs">License #</p>
                                    <p className="text-slate-700">{agent.license_number}</p>
                                  </div>
                                )}
                              </div>
                              
                              {agent.notes && (
                                <p className="text-sm text-slate-600 mt-3 bg-slate-50 p-2 rounded">
                                  {agent.notes}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex gap-2 ml-4">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openEditAgentDialog(agent)}
                                data-testid={`edit-agent-${agent.agent_id}`}
                              >
                                <Pencil className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleDeleteAgent(agent)}
                                data-testid={`delete-agent-${agent.agent_id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Claims by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(analytics.claims_by_type || {}).length === 0 ? (
                      <p className="text-slate-500">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(analytics.claims_by_type).map(([type, count]) => (
                          <div key={type} className="flex items-center justify-between">
                            <span>{getClaimTypeName(type)}</span>
                            <Badge variant="outline">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Claims by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(analytics.claims_by_status || {}).length === 0 ? (
                      <p className="text-slate-500">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(analytics.claims_by_status).map(([status, count]) => (
                          <div key={status} className="flex items-center justify-between">
                            <Badge className={STATUS_COLORS[status]}>{STATUS_NAMES[status]}</Badge>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Key Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Average Resolution Time</span>
                      <span className="font-semibold">{analytics.avg_resolution_days} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Most Common Claim</span>
                      <span className="font-semibold">{getClaimTypeName(analytics.most_common_type) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Claims (Last 30 Days)</span>
                      <span className="font-semibold">{analytics.recent_claims_30d}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Claim Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Claim {selectedClaim?.claim_reference}
                <Badge className={STATUS_COLORS[selectedClaim?.status]}>
                  {STATUS_NAMES[selectedClaim?.status]}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            {selectedClaim && (
              <div className="space-y-6 py-4">
                {/* Actions */}
                <div className="flex gap-2">
                  <Button onClick={() => { setStatusUpdate({ status: '', note: '', notify_submitter: true }); setShowStatusDialog(true); }}>
                    Update Status
                  </Button>
                  {!selectedClaim.assigned_contractor_id && (
                    <Button variant="outline" onClick={() => { setAssignData({ contractor_id: '', repair_scheduled_date: '', notes: '' }); setShowAssignDialog(true); }}>
                      <Wrench className="w-4 h-4 mr-2" />
                      Assign Contractor
                    </Button>
                  )}
                </div>

                {/* Submitter & Claim Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold">Submitter</h4>
                    <div className="text-sm space-y-2">
                      <p><User className="w-4 h-4 inline mr-2" />{selectedClaim.submitter_name}</p>
                      <p><Mail className="w-4 h-4 inline mr-2" />{selectedClaim.submitter_email}</p>
                      {selectedClaim.submitter_phone && (
                        <p><Phone className="w-4 h-4 inline mr-2" />{selectedClaim.submitter_phone}</p>
                      )}
                      <p><Building2 className="w-4 h-4 inline mr-2" />Unit {selectedClaim.unit_number}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold">Claim Details</h4>
                    <div className="text-sm space-y-2">
                      <p><strong>Type:</strong> {getClaimTypeName(selectedClaim.claim_type)}</p>
                      <p><strong>Incident Date:</strong> {formatDate(selectedClaim.incident_date)}</p>
                      {selectedClaim.incident_location && (
                        <p><strong>Location:</strong> {selectedClaim.incident_location}</p>
                      )}
                      {selectedClaim.estimated_value && (
                        <p><strong>Estimated Value:</strong> {formatCurrency(selectedClaim.estimated_value)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedClaim.incident_description}</p>
                </div>

                {/* Contractor Info */}
                {selectedClaim.assigned_contractor_name && (
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Wrench className="w-4 h-4" />
                      Assigned Contractor
                    </h4>
                    <p className="text-sm">{selectedClaim.assigned_contractor_name}</p>
                    {selectedClaim.repair_scheduled_date && (
                      <p className="text-sm text-slate-600">Scheduled: {formatDate(selectedClaim.repair_scheduled_date)}</p>
                    )}
                  </div>
                )}

                {/* Uploaded Files */}
                {(selectedClaim.photos?.length > 0 || selectedClaim.videos?.length > 0 || selectedClaim.documents?.length > 0) && (
                  <div>
                    <h4 className="font-semibold mb-2">Evidence Files</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedClaim.photos?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1 text-sm bg-slate-100 px-3 py-1 rounded hover:bg-slate-200">
                          <Image className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                      {selectedClaim.videos?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1 text-sm bg-slate-100 px-3 py-1 rounded hover:bg-slate-200">
                          <Video className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                      {selectedClaim.documents?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1 text-sm bg-slate-100 px-3 py-1 rounded hover:bg-slate-200">
                          <FileText className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contractor Uploads */}
                {selectedClaim.contractor_uploads?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Contractor Uploads</h4>
                    <div className="space-y-2">
                      {selectedClaim.contractor_uploads.map((u, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 p-3 rounded">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{u.upload_type}</Badge>
                            <a href={`${API_URL}${u.file_url}`} target="_blank" rel="noopener noreferrer"
                               className="text-sm text-blue-600 hover:underline">
                              {u.file_name}
                            </a>
                          </div>
                          {u.amount && <span className="font-semibold">{formatCurrency(u.amount)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div>
                  <h4 className="font-semibold mb-2">Status Timeline</h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedClaim.status_history?.map((entry, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${i === 0 ? 'bg-indigo-600' : 'bg-slate-300'}`} />
                          {i < selectedClaim.status_history.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-3">
                          <p className="font-medium text-sm">{STATUS_NAMES[entry.status] || entry.status}</p>
                          <p className="text-xs text-slate-600">{entry.note}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDateTime(entry.timestamp)} • {entry.updated_by}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Status Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Update Claim Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>New Status *</Label>
                <Select value={statusUpdate.status} onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {claimStatuses.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea
                  value={statusUpdate.note}
                  onChange={(e) => setStatusUpdate({...statusUpdate, note: e.target.value})}
                  placeholder="Add a note about this status change..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify"
                  checked={statusUpdate.notify_submitter}
                  onChange={(e) => setStatusUpdate({...statusUpdate, notify_submitter: e.target.checked})}
                />
                <Label htmlFor="notify" className="cursor-pointer">Notify claim submitter</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateStatus}>Update Status</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Contractor Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Contractor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Contractor *</Label>
                <Select value={assignData.contractor_id} onValueChange={(v) => setAssignData({...assignData, contractor_id: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.contractor_id} value={c.contractor_id}>
                        {c.company_name || c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scheduled Repair Date</Label>
                <Input
                  type="date"
                  value={assignData.repair_scheduled_date}
                  onChange={(e) => setAssignData({...assignData, repair_scheduled_date: e.target.value})}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={assignData.notes}
                  onChange={(e) => setAssignData({...assignData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button onClick={handleAssignContractor}>Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Insurance Agent Dialog */}
        <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                {editingAgent ? 'Edit Insurance Agent' : 'Add Insurance Agent'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <Label>Agent Name *</Label>
                  <Input
                    value={agentForm.agent_name}
                    onChange={(e) => setAgentForm({...agentForm, agent_name: e.target.value})}
                    placeholder="John Smith"
                    className="mt-1"
                    data-testid="agent-name-input"
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <Label>Insurance Company *</Label>
                  <Input
                    value={agentForm.company_name}
                    onChange={(e) => setAgentForm({...agentForm, company_name: e.target.value})}
                    placeholder="ABC Insurance Ltd"
                    className="mt-1"
                    data-testid="agent-company-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                    placeholder="agent@insurance.com"
                    className="mt-1"
                    data-testid="agent-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={agentForm.phone}
                    onChange={(e) => setAgentForm({...agentForm, phone: e.target.value})}
                    placeholder="+27 21 555 0000"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>License Number</Label>
                  <Input
                    value={agentForm.license_number}
                    onChange={(e) => setAgentForm({...agentForm, license_number: e.target.value})}
                    placeholder="FSP12345"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Policy Number</Label>
                  <Input
                    value={agentForm.policy_number}
                    onChange={(e) => setAgentForm({...agentForm, policy_number: e.target.value})}
                    placeholder="POL-2024-00001"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Policy Type</Label>
                  <Select 
                    value={agentForm.policy_type} 
                    onValueChange={(v) => setAgentForm({...agentForm, policy_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comprehensive">Comprehensive</SelectItem>
                      <SelectItem value="fire">Fire Only</SelectItem>
                      <SelectItem value="liability">Liability</SelectItem>
                      <SelectItem value="strata">Strata/Sectional Title</SelectItem>
                      <SelectItem value="building">Building Only</SelectItem>
                      <SelectItem value="contents">Contents</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Coverage Amount (R)</Label>
                  <Input
                    type="number"
                    value={agentForm.coverage_amount}
                    onChange={(e) => setAgentForm({...agentForm, coverage_amount: e.target.value})}
                    placeholder="5000000"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Policy Start Date</Label>
                  <Input
                    type="date"
                    value={agentForm.policy_start_date}
                    onChange={(e) => setAgentForm({...agentForm, policy_start_date: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Policy Expiry Date</Label>
                  <Input
                    type="date"
                    value={agentForm.policy_expiry_date}
                    onChange={(e) => setAgentForm({...agentForm, policy_expiry_date: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={agentForm.notes}
                  onChange={(e) => setAgentForm({...agentForm, notes: e.target.value})}
                  placeholder="Additional notes about this insurance agent or policy..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAgentDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveAgent} disabled={savingAgent} data-testid="save-agent-btn">
                {savingAgent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingAgent ? 'Update Agent' : 'Add Agent'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
